/* Aliangé Quiz Popup — slide-in offer card + collapsible pill.
   See sections/quiz-popup.liquid for markup and assets/quiz-popup.css for styles.

   2026-10-21 (first-order gift build, spec §4.2):
   - The card is non-modal: no overlay, no scroll lock, no focus trap. It slides
     in bottom-right and the page stays usable.
   - X / Esc / "Not now" COLLAPSE the card into the pill. The pill persists 7 days
     (localStorage aliange_offer_pill). Clicking the pill re-expands the card.
   - The pill's x hides everything for 30 days.
   - Quiz completers (aliange_quiz_completed) never auto-expand: pill only. Their
     card CTA applies PRESCRIBE15 and goes to the Protocols.
   - Retired: the 2026-07-27 dismiss cookie, dismiss-count backstop and bottom-left
     sticky tab. Kept: automatic-open floors, path suppression, cart/quiz drawer
     hiding, the openQuiz() in-place flow and the three original event names.

   2026-10-29: hidden on all product URLs and once the cart has items. */
(function () {
  'use strict';

  // Universal first-party signal — set by the quiz itself (assets/bundled-quiz.js)
  // on submit success. One canonical cookie, 1y expiry. The popup NEVER sets it.
  var COOKIE_COMPLETE = 'aliange_quiz_completed';
  // Once-per-session cap — set on first auto-open so the card never re-fires on later
  // pages of the same session (timer + scroll + exit-intent all share this gate).
  var SESSION_SHOWN_KEY = 'aliange_quiz_popup_shown_session';
  var SESSION_PILL_KEY = 'aliange_quiz_popup_sticky_shown_session';

  // { state: 'pill' | 'closed', ts }. 'pill' lasts 7 days, 'closed' 30.
  var PILL_KEY = 'aliange_offer_pill';
  var PILL_DAYS = 7;
  var CLOSED_DAYS = 30;

  // ── Automatic-open floors (2026-09-08, site-performance batch) ─────────────────
  // Both conditions must hold, and these floors apply even if the section settings
  // say something lower, because config/*.json and templates/*.json are
  // Shopify-editor-owned and do not reliably sync from git
  // (memory: feedback_shopify-json-template-sync-gap).
  var MIN_TRIGGER_DELAY_S = 8;
  var MIN_SCROLL_PERCENT = 40;

  var COLLAPSE_MS = 260;

  var DRAWER_SELECTOR = '#quiz-drawer'; // AliangeQuiz drawer; open when aria-hidden="false"
  // Theme cart drawer (<cart-drawer id="cart-drawer">, carries `open` while shown).
  // Neither the card nor the pill may cover CHECKOUT (H-2026-09-26-01).
  var CART_DRAWER_SELECTOR = '#cart-drawer';

  var SUPPRESSED_PATHS = [
    /^\/pages\/quiz/i,
    /^\/cart/i,
    /^\/checkout/i,
    /^\/account/i,
    // PDPs (2026-09-27, H-2026-09-26-01 item 1): the pill would sit on the PDP
    // sticky ADD TO CART bar.
    /(^|\/)products\//i,
  ];

  // Tracking — fires to Klaviyo (modern API + _learnq fallback) and GA4 dataLayer.
  // Wrapped in try/catch so any tracking failure can never block the popup itself.
  function track(eventName, props) {
    try {
      var data = props || {};
      if (window.klaviyo && typeof window.klaviyo.track === 'function') {
        window.klaviyo.track(eventName, data);
      } else if (window._learnq) {
        window._learnq.push(['track', eventName, data]);
      }
    } catch (e) { /* ignore */ }
    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: eventName.replace(/\s+/g, '_').toLowerCase(), event_category: 'quiz_popup', ...props });
    } catch (e) { /* ignore */ }
  }

  function getCookie(name) {
    var match = document.cookie.match(new RegExp('(?:^|;\\s*)' + name + '=([^;]*)'));
    return match ? decodeURIComponent(match[1]) : null;
  }

  function isSuppressedPath() {
    var path = window.location.pathname || '';
    for (var i = 0; i < SUPPRESSED_PATHS.length; i++) {
      if (SUPPRESSED_PATHS[i].test(path)) return true;
    }
    return false;
  }

  function getSessionFlag(key) {
    try { return window.sessionStorage.getItem(key) === '1'; }
    catch (e) { return false; }
  }

  function setSessionFlag(key) {
    try { window.sessionStorage.setItem(key, '1'); } catch (e) { /* ignore */ }
  }

  // Returns 'pill', 'closed' or null (unset / expired / storage blocked).
  function getPillState() {
    try {
      var raw = JSON.parse(window.localStorage.getItem(PILL_KEY) || 'null');
      if (!raw || !raw.ts) return null;
      var days = raw.state === 'closed' ? CLOSED_DAYS : PILL_DAYS;
      if (Date.now() - raw.ts > days * 864e5) return null;
      return raw.state === 'closed' || raw.state === 'pill' ? raw.state : null;
    } catch (e) { return null; }
  }

  function setPillState(state) {
    try { window.localStorage.setItem(PILL_KEY, JSON.stringify({ state: state, ts: Date.now() })); }
    catch (e) { /* ignore */ }
  }

  // Percentage of the page scrolled (0–100). 0 when the page is too short to scroll.
  function scrollPercent() {
    var doc = document.documentElement;
    var max = (doc.scrollHeight || document.body.scrollHeight || 0) - doc.clientHeight;
    if (max <= 0) return 0;
    var top = window.pageYOffset || doc.scrollTop || 0;
    return (top / max) * 100;
  }

  function isMobileViewport() {
    return window.matchMedia && window.matchMedia('(max-width: 767px)').matches;
  }

  function isCartOpen() {
    var c = document.querySelector(CART_DRAWER_SELECTOR);
    return !!(c && c.hasAttribute('open'));
  }

  function isDrawerOpen() {
    var d = document.querySelector(DRAWER_SELECTOR);
    return !!(d && d.getAttribute('aria-hidden') === 'false');
  }

  function init() {
    var popup = document.getElementById('quiz-popup');
    if (!popup) return;
    var pill = document.getElementById('quiz-offer-pill');
    var pillOpen = document.getElementById('quiz-sticky-tab');

    var enableMobile = popup.getAttribute('data-enable-mobile') === 'true';
    var enableExitIntent = popup.getAttribute('data-enable-exit-intent') === 'true';
    var triggerDelay = parseInt(popup.getAttribute('data-trigger-delay'), 10);
    if (isNaN(triggerDelay) || triggerDelay < 0) triggerDelay = MIN_TRIGGER_DELAY_S;
    triggerDelay = Math.max(triggerDelay, MIN_TRIGGER_DELAY_S);
    var scrollTarget = parseInt(popup.getAttribute('data-scroll-percent'), 10);
    if (isNaN(scrollTarget) || scrollTarget <= 0) scrollTarget = MIN_SCROLL_PERCENT;
    scrollTarget = Math.max(scrollTarget, MIN_SCROLL_PERCENT);
    var ctaUrl = popup.getAttribute('data-cta-url') || '/pages/quiz';
    var completedCtaUrl = popup.getAttribute('data-completed-cta-url') || '/collections/protocols';

    // Hard gates — suppress both the card and the pill.
    if (!enableMobile && isMobileViewport()) return;
    if (isSuppressedPath()) return;
    if (getPillState() === 'closed') return;
    // Guests and $0-spend customers only: enforced in Liquid.

    var completed = !!getCookie(COOKIE_COMPLETE);
    var cta = popup.querySelector('[data-quiz-popup-cta]');

    // First-order gift end gate (2026-10-30): a cached page can still carry the
    // Exfoliator copy, so re-check the clock (window.Aliange.gift, layout/theme.liquid).
    var giftEnded = !!(window.Aliange && window.Aliange.gift && window.Aliange.gift.ended());
    if (giftEnded) window.Aliange.gift.apply(popup);

    // Completers already hold the code: swap the card to the shop path.
    if (completed) {
      if (cta && cta.getAttribute('data-completed-label')) cta.textContent = cta.getAttribute('data-completed-label');
      var sub = popup.querySelector('[data-quiz-popup-subhead]');
      var tpl = (giftEnded && popup.querySelector('template[data-quiz-popup-completed-subhead-ended]')) ||
        popup.querySelector('template[data-quiz-popup-completed-subhead]');
      if (sub && tpl) sub.innerHTML = tpl.innerHTML;
      var pillLabel = pillOpen && ((giftEnded && pillOpen.getAttribute('data-label-completed-ended')) || pillOpen.getAttribute('data-label-completed'));
      if (pillLabel) pillOpen.textContent = pillLabel;
    }

    // Open the quiz drawer in place. Fallback: navigate with ?open-quiz=true so it
    // auto-opens on arrival. Returns true when opened inline (no navigation).
    function openQuizFlow(entry) {
      entry = entry || 'popup';
      if (window.AliangeQuiz && window.AliangeQuiz.ui && typeof window.AliangeQuiz.ui.openQuiz === 'function') {
        window.AliangeQuiz.ui.openQuiz(entry);
        return true;
      }
      var dest = ctaUrl + (ctaUrl.indexOf('?') === -1 ? '?' : '&') + 'open-quiz=true&quiz_entry=' + encodeURIComponent(entry);
      setTimeout(function () { window.location.href = dest; }, 80);
      return false;
    }

    // ---- View state: 'none' | 'card' | 'pill' ----
    var view = 'none';
    var pillWanted = false; // this page wants the pill (may be hidden while a drawer is open)

    // 2026-10-29: once the cart has items the offer has done its job. Liquid skips
    // rendering when the cart is non-empty at page load; this covers adds during
    // the page view and bfcache restores. Stays hidden for the rest of the page view.
    var cartHasItems = false;
    function blocked() { return cartHasItems || isCartOpen() || isDrawerOpen(); }

    function syncPill() {
      if (!pill) return;
      var show = pillWanted && view === 'pill' && !blocked();
      pill.hidden = !show;
      if (show && !getSessionFlag(SESSION_PILL_KEY)) {
        setSessionFlag(SESSION_PILL_KEY);
        track('Quiz Popup Sticky Shown', { page_path: window.location.pathname, completed: completed });
      }
    }

    function showPill() {
      view = 'pill';
      pillWanted = true;
      if (pillOpen) pillOpen.setAttribute('aria-expanded', 'false');
      syncPill();
    }

    function expand(trigger) {
      if (view === 'card') return;
      if (blocked()) return;
      view = 'card';
      if (pill) pill.hidden = true;
      if (pillOpen) pillOpen.setAttribute('aria-expanded', 'true');
      popup.removeAttribute('data-collapsing');
      popup.hidden = false;
      popup.setAttribute('aria-hidden', 'false');
      requestAnimationFrame(function () {
        requestAnimationFrame(function () { popup.setAttribute('data-open', 'true'); });
      });
      document.addEventListener('keydown', onKeydown, true);
      // A visitor-initiated expand takes focus; an automatic slide-in does not
      // (non-modal: it must not pull focus away from what they are reading).
      if (trigger === 'pill' && cta && typeof cta.focus === 'function') {
        setTimeout(function () { cta.focus({ preventScroll: true }); }, 60);
      }
    }

    function hideCard(animateToPill) {
      document.removeEventListener('keydown', onKeydown, true);
      popup.setAttribute('aria-hidden', 'true');
      if (animateToPill) popup.setAttribute('data-collapsing', 'true');
      popup.removeAttribute('data-open');
      setTimeout(function () {
        if (view !== 'card') {
          popup.hidden = true;
          popup.removeAttribute('data-collapsing');
        }
      }, COLLAPSE_MS + 40);
    }

    function onCartCount(count) {
      if (cartHasItems || !(count > 0)) return;
      cartHasItems = true;
      // Set view first: hideCard() only sets popup.hidden once view !== 'card'.
      if (view === 'card') { view = 'none'; hideCard(false); }
      syncPill();
    }

    function checkCart() {
      if (cartHasItems || !window.fetch) return;
      fetch('/cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (c) { if (c) onCartCount(c.item_count); })
        .catch(function () { /* ignore */ });
    }

    // Theme cart events (Prestige dispatches cart:change with detail.cart).
    document.addEventListener('cart:change', function (e) {
      var c = e && e.detail && e.detail.cart;
      if (c && typeof c.item_count === 'number') onCartCount(c.item_count);
      else checkCart();
    });

    // Theme-agnostic backstop: any AJAX cart write (add/change/update) → re-read the cart.
    if (window.PerformanceObserver) {
      try {
        new PerformanceObserver(function (list) {
          var entries = list.getEntries();
          for (var j = 0; j < entries.length; j++) {
            if (/\/cart\/(add|change|update)(\.js)?(\?|$)/.test(entries[j].name)) { checkCart(); return; }
          }
        }).observe({ type: 'resource', buffered: false });
      } catch (e) { /* ignore */ }
    }

    // Back/forward cache: page restored after adding to cart elsewhere.
    window.addEventListener('pageshow', function (e) { if (e.persisted) checkCart(); });

    // X / Esc / "Not now": collapse into the pill, remembered for 7 days.
    function collapse(reason) {
      if (view !== 'card') return;
      var hadFocus = popup.contains(document.activeElement);
      hideCard(true);
      showPill();
      setPillState('pill');
      track('Quiz Popup Dismissed', { reason: reason || 'unknown', collapsed: true });
      track('Quiz Popup Collapsed', { reason: reason || 'unknown', page_path: window.location.pathname });
      if (hadFocus && pillOpen && !pill.hidden) {
        try { pillOpen.focus({ preventScroll: true }); } catch (e) { /* ignore */ }
      }
    }

    function onKeydown(e) {
      if (e.key === 'Escape' || e.key === 'Esc') {
        e.preventDefault();
        collapse('esc');
      }
    }

    var dismissEls = popup.querySelectorAll('[data-quiz-popup-dismiss]');
    for (var i = 0; i < dismissEls.length; i++) {
      dismissEls[i].addEventListener('click', function (e) {
        e.preventDefault();
        collapse(this.classList.contains('quiz-popup__later') ? 'not_now' : 'close_button');
      });
    }

    if (cta) {
      cta.addEventListener('click', function (e) {
        e.preventDefault();
        if (completed) {
          track('Quiz Popup CTA Clicked', { destination: completedCtaUrl, completed: true });
          window.location.href = completedCtaUrl;
          return;
        }
        hideCard(false);
        view = 'pill';
        pillWanted = true;
        var inline = openQuizFlow('popup');
        track('Quiz Popup CTA Clicked', { destination: inline ? 'inline_drawer' : ctaUrl });
        // If they abandon the drawer, the pill returns once it closes (observer).
        if (!inline) pillWanted = false;
        syncPill();
      });
    }

    if (pillOpen) {
      pillOpen.addEventListener('click', function (e) {
        e.preventDefault();
        track('Quiz Popup Pill Clicked', { page_path: window.location.pathname, completed: completed });
        expand('pill');
      });
    }

    var pillClose = pill ? pill.querySelector('[data-quiz-pill-close]') : null;
    if (pillClose) {
      pillClose.addEventListener('click', function (e) {
        e.preventDefault();
        setPillState('closed');
        pillWanted = false;
        view = 'none';
        pill.hidden = true;
        track('Quiz Popup Pill Closed', { page_path: window.location.pathname, suppress_days: CLOSED_DAYS });
      });
    }

    // Hide the card and pill while the cart or quiz drawer is open; restore after.
    function onDrawerChange() {
      if (isCartOpen()) checkCart();
      if (getCookie(COOKIE_COMPLETE) && !completed) {
        // Completed in place just now: the drawer's result screen carries the code.
        // Stop interrupting for this page view.
        pillWanted = false;
      }
      if (blocked() && view === 'card') {
        track('Quiz Popup Closed For Cart', { page_path: window.location.pathname });
        hideCard(false);
        view = 'pill';
        pillWanted = true;
      }
      syncPill();
    }
    if (window.MutationObserver) {
      var drawer = document.querySelector(DRAWER_SELECTOR);
      if (drawer) new MutationObserver(onDrawerChange).observe(drawer, { attributes: true, attributeFilter: ['aria-hidden'] });
      var cart = document.querySelector(CART_DRAWER_SELECTOR);
      if (cart) new MutationObserver(onDrawerChange).observe(cart, { attributes: true, attributeFilter: ['open'] });
    }

    // ---- Entry: pill only, or arm the automatic slide-in ----
    if (completed || getPillState() === 'pill' || getSessionFlag(SESSION_SHOWN_KEY)) {
      showPill();
      return;
    }

    // 2026-11-03 (Hannah): no automatic slide-in while the Klaviyo Black Friday
    // sign-up popup runs. Click entries are untouched. Set AUTO_OPEN = true to restore.
    var AUTO_OPEN = false;
    if (!AUTO_OPEN) return;

    var dwellMet = false;
    var delayTimer = null;
    var scrollListener = null;
    var exitListener = null;

    function cleanupTriggers() {
      if (delayTimer) { clearTimeout(delayTimer); delayTimer = null; }
      if (scrollListener) { window.removeEventListener('scroll', scrollListener); scrollListener = null; }
      if (exitListener) { document.removeEventListener('mouseout', exitListener); exitListener = null; }
    }

    function autoOpen(trigger) {
      if (view !== 'none') return;
      if (blocked()) return; // never over the cart or the quiz
      setSessionFlag(SESSION_SHOWN_KEY);
      cleanupTriggers();
      expand(trigger);
      track('Quiz Popup Shown', { trigger: trigger || 'time_delay', page_path: window.location.pathname });
    }

    // Dwell AND depth, not raced (2026-09-08): either alone is a weak signal, and
    // racing them put the card on screen mid-paint.
    function maybeOpen(trigger) {
      if (!dwellMet) return;
      if (scrollPercent() < scrollTarget) return;
      autoOpen(trigger);
    }

    delayTimer = setTimeout(function () {
      dwellMet = true;
      maybeOpen('time_delay');
    }, triggerDelay * 1000);

    scrollListener = function () { maybeOpen('scroll_depth'); };
    window.addEventListener('scroll', scrollListener, { passive: true });

    // Exit-intent: desktop only, respects the dwell floor but not the depth gate.
    if (enableExitIntent && !isMobileViewport()) {
      exitListener = function (e) {
        if (!dwellMet) return;
        if (e.clientY <= 4 && e.relatedTarget == null) autoOpen('exit_intent');
      };
      document.addEventListener('mouseout', exitListener);
    }
  }

  // Lazy-init: DOMContentLoaded + 1s
  function schedule() {
    setTimeout(init, 1000);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', schedule, { once: true });
  } else {
    schedule();
  }
})();
